const DEFAULT_USER_FACING_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Cloud Functions `validateArgs` throws `HttpsError` with messages like
 * `Invalid data: ${JSON.stringify(zodError.errors)}` — unsuitable for end users.
 * Returns a safe fallback instead of exposing that payload or other technical dumps.
 */
export function sanitizeCallableErrorMessage(
  error: unknown,
  fallbackMessage: string = DEFAULT_USER_FACING_MESSAGE,
): string {
  const wrapped = coerceToError(error);
  if (!wrapped) {
    return fallbackMessage;
  }

  const message = wrapped.message ?? '';
  const code = getErrorCodeFromAny(error, wrapped);

  if (isZodInvalidDataMessage(message)) {
    return fallbackMessage;
  }

  if (code === 'functions/internal') {
    return fallbackMessage;
  }

  if (looksLikeSerializedValidationPayload(message)) {
    return fallbackMessage;
  }

  if (
    code === 'functions/invalid-argument' &&
    looksLikeTechnicalInvalidArgument(message)
  ) {
    return fallbackMessage;
  }

  return message;
}

/** Sanitize a raw message string (e.g. from loader data) without an Error instance. */
export function sanitizeUserFacingString(
  message: string,
  fallbackMessage: string = DEFAULT_USER_FACING_MESSAGE,
): string {
  return sanitizeCallableErrorMessage(new Error(message), fallbackMessage);
}

function coerceToError(error: unknown): Error | null {
  if (error instanceof Error) {
    return error;
  }
  const msg = getMessageFromUnknown(error);
  if (msg !== undefined) {
    return new Error(msg);
  }
  return null;
}

function getMessageFromUnknown(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === 'string') {
      return m;
    }
  }
  return undefined;
}

function getErrorCodeFromAny(original: unknown, asError: Error): string | undefined {
  if ('code' in asError && typeof (asError as { code?: unknown }).code === 'string') {
    return (asError as { code: string }).code;
  }
  if (original && typeof original === 'object' && 'code' in original) {
    const c = (original as { code?: unknown }).code;
    if (typeof c === 'string') {
      return c;
    }
  }
  return undefined;
}

function isZodInvalidDataMessage(message: string): boolean {
  return /Invalid data:/i.test(message);
}

function looksLikeSerializedValidationPayload(message: string): boolean {
  if (message.length < 40) {
    return false;
  }
  const t = message.trim();
  if (t.startsWith('[') && t.includes('"code"') && t.includes('"path"')) {
    return true;
  }
  if (t.startsWith('{') && t.includes('"issues"')) {
    return true;
  }
  if (
    message.length >= 80 &&
    message.includes('"path"') &&
    message.includes('"code"') &&
    (message.includes('"expected"') || message.includes('invalid_type'))
  ) {
    return true;
  }
  return false;
}

/** Long invalid-argument payloads are usually validation dumps, not user-facing copy. */
function looksLikeTechnicalInvalidArgument(message: string): boolean {
  return message.length > 200 && (message.includes('[') || message.includes('{'));
}
